/**
 * VerifyEmailConsumePage — token-consume side of the `/verify-email`
 * flow. The router-level dispatcher pulls `?token=…` from the URL and
 * passes it as a prop; this page validates the 64-hex format on mount,
 * auto-POSTs `POST /auth/verify-email` (Public + CsrfExempt — the
 * token is the credential), and renders the result.
 *
 * No user input: unlike password-reset / accept-invitation, the verify-
 * email flow has nothing to ask the user — the token alone carries
 * the entire transaction. The page submits on mount and shows one of:
 *
 *   - loading: in-flight
 *   - success: 200 → "Email verified" + Sign-in link
 *   - invalid-link: 404/410/malformed-format → unified anti-enum surface
 *   - temporary-failure: 429/5xx/network → Retry button to re-submit
 *
 * Anti-enum FE policy: BE differentiates 404 (token not found) and 410
 * (token expired), but the FE collapses both into ONE invalid surface
 * with no leaked reason copy. The BE leak is bounded to the audit log;
 * the FE refuses to amplify it via UX.
 *
 * Tokens-only colors via @sparta/design-tokens; touch targets ≥ 44 px;
 * `role="status"` aria-live regions; semantic landmarks per CLAUDE.md
 * Frontend rule §7 (WCAG 2.1 AA).
 */

import * as React from 'react';
import { verifyEmailBodySchema } from '@artemis/validators';
import { Button, Card } from '@sparta/ui-kit';
import { apiClient } from '../../shared/api-client.js';

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'success' }
  | { kind: 'invalid-link' }
  | { kind: 'temporary-failure'; message: string };

export interface VerifyEmailConsumePageProps {
  /**
   * Raw token from the `?token=` query param. The page validates the
   * 64-hex format up front; a malformed token short-circuits to the
   * invalid-link surface without ever hitting the network.
   */
  token: string;
}

export function VerifyEmailConsumePage({
  token,
}: VerifyEmailConsumePageProps): React.ReactElement {
  // Token-format check reuses the canonical regex from the wire schema
  // so the FE never drifts from the BE contract.
  const tokenFormatValid = React.useMemo(
    () => verifyEmailBodySchema.shape.token.safeParse(token).success,
    [token],
  );

  const [state, setState] = React.useState<SubmitState>(() =>
    tokenFormatValid ? { kind: 'submitting' } : { kind: 'invalid-link' },
  );

  const submit = React.useCallback(async (): Promise<void> => {
    setState({ kind: 'submitting' });
    try {
      const result = await apiClient.POST('/auth/verify-email', {
        body: { token },
      });
      const { response } = result;
      if (response.ok) {
        setState({ kind: 'success' });
        return;
      }
      if (response.status === 429) {
        setState({
          kind: 'temporary-failure',
          message: 'Too many attempts. Please wait a moment and try again.',
        });
        return;
      }
      if (response.status >= 400 && response.status < 500) {
        // All 4xx (except 429 above) collapse to the same anti-enum
        // "invalid link" surface: 404 token-not-found, 410 expired,
        // 422 body-validation-rejected, 400 generic — to the user
        // they all mean the link is no good; the FE refuses to
        // amplify the BE differentiation via UX.
        setState({ kind: 'invalid-link' });
        return;
      }
      if (response.status >= 500) {
        setState({
          kind: 'temporary-failure',
          message:
            'Email verification is temporarily unavailable. Try again shortly.',
        });
        return;
      }
      setState({
        kind: 'temporary-failure',
        message: 'Something went wrong. Try again.',
      });
    } catch {
      setState({
        kind: 'temporary-failure',
        message: 'Network error. Check your connection and try again.',
      });
    }
  }, [token]);

  // Intentionally mount-once. Retry is driven explicitly by the user
  // via the Try-again button below — listing `submit` in the dep array
  // would re-fire on every render through React's strict-mode double-
  // invocation cycles. The token prop is stable for the page's lifetime
  // (it is read once from the URL by VerifyEmailRoute and never
  // mutated), so capturing the initial closure is safe.
  const hasSubmittedRef = React.useRef(false);
  React.useEffect(() => {
    if (!tokenFormatValid) return;
    if (hasSubmittedRef.current) return;
    hasSubmittedRef.current = true;
    void submit();
  }, [tokenFormatValid, submit]);

  return (
    <main
      className="min-h-screen w-full"
      style={{ background: 'var(--bg-canvas)', color: 'var(--fg-primary)' }}
    >
      <div className="mx-auto max-w-md px-4 py-12 sm:py-16">
        {state.kind === 'submitting' || state.kind === 'idle' ? (
          <LoadingSurface />
        ) : state.kind === 'success' ? (
          <SuccessSurface />
        ) : state.kind === 'invalid-link' ? (
          <InvalidLinkSurface />
        ) : (
          <TemporaryFailureSurface
            message={state.message}
            onRetry={() => {
              void submit();
            }}
          />
        )}
      </div>
    </main>
  );
}

// ===========================================================================
// Surfaces
// ===========================================================================

function LoadingSurface(): React.ReactElement {
  return (
    <section role="status" aria-live="polite">
      <p
        className="text-sm"
        style={{ color: 'var(--fg-tertiary)' }}
      >
        Verifying your email…
      </p>
    </section>
  );
}

function SuccessSurface(): React.ReactElement {
  return (
    <Card asChild variant="surface" padding="md">
      <section role="status" aria-live="polite">
        <h1
          className="text-3xl font-bold"
          style={{ letterSpacing: '-0.6px' }}
        >
          Email verified
        </h1>
        <p
          className="mt-3 text-sm"
          style={{ color: 'var(--fg-tertiary)' }}
        >
          Your email address is verified. Sign in to continue.
        </p>
        <a
          href="/login"
          className="mt-5 inline-flex min-h-touch items-center text-[13px] font-semibold no-underline"
          style={{ color: 'var(--fg-brand)' }}
        >
          Sign in →
        </a>
      </section>
    </Card>
  );
}

function InvalidLinkSurface(): React.ReactElement {
  return (
    <Card asChild variant="surface" padding="md">
      <section role="status" aria-live="polite">
        <h1
          className="text-3xl font-bold"
          style={{ letterSpacing: '-0.6px' }}
        >
          This verification link is not valid
        </h1>
        <p
          className="mt-3 text-sm"
          style={{ color: 'var(--fg-tertiary)' }}
        >
          This verification link can no longer be used. Sign in to request
          a new verification email from your account settings.
        </p>
        <a
          href="/login"
          className="mt-5 inline-flex min-h-touch items-center text-[13px] font-semibold no-underline"
          style={{ color: 'var(--fg-brand)' }}
        >
          Go to sign in →
        </a>
      </section>
    </Card>
  );
}

function TemporaryFailureSurface(props: {
  message: string;
  onRetry: () => void;
}): React.ReactElement {
  return (
    <Card asChild variant="surface" padding="md">
      <section role="status" aria-live="polite">
        <h1
          className="text-3xl font-bold"
          style={{ letterSpacing: '-0.6px' }}
        >
          We couldn't verify just yet
        </h1>
        <p
          className="mt-3 text-sm"
          style={{ color: 'var(--fg-danger)' }}
        >
          {props.message}
        </p>
        <div className="mt-5">
          <Button
            type="button"
            variant="primary"
            size="md"
            onClick={props.onRetry}
          >
            Try again
          </Button>
        </div>
      </section>
    </Card>
  );
}
